import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useApexOnlyPage } from '../../hooks/useApexOnlyPage';
import { buildApexCanonicalUrl } from '../../lib/siteUrls';

const PrivacyPolicy = () => {
  const isRedirecting = useApexOnlyPage('/privacy');

  if (isRedirecting) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Privacy Policy | Alakaʻi Aloha</title>
        <meta
          name="description"
          content="Read the Alakaʻi Aloha privacy policy for our apex domain, island subdomains, and trip-planning services."
        />
        <link rel="canonical" href={buildApexCanonicalUrl('/privacy')} />
      </Helmet>

      <div className="max-w-4xl mx-auto py-4 px-6 sm:px-8">
        <header className="mb-8 space-y-2">
          <h1 className="text-3xl font-semibold">Privacy Policy</h1>
          <p className="text-base leading-relaxed text-white/80">
            This Privacy Policy explains what we collect, how we use it, who we share it with, and the choices you
            have. It applies to alakaialoha.com, our island subdomains, and our trip-planning services (the
            &ldquo;Service&rdquo;).
          </p>
          <p className="text-sm text-white/60">
            <span className="font-medium">Last updated:</span> October 5, 2025
          </p>
        </header>

        {/* 1) What we collect */}
        <section className="space-y-5 text-sm leading-relaxed text-white/80">
          <h2 className="text-xl font-semibold">1. Information We Collect</h2>
          <p>We do not offer passworded user accounts. Your interview and itinerary are tied to unique access links/IDs.</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Interview responses:</strong> trip dates, group makeup, interests, constraints, preferences,
              and other details you provide to personalize an itinerary.
            </li>
            <li>
              <strong>Itinerary artifacts:</strong> generated schedules, dining/activity suggestions, and related
              links.
            </li>
            <li>
              <strong>Device & usage data:</strong> basic logs (IP address, timestamps, pages viewed, referrer),
              error diagnostics, and performance metrics used to keep the Service reliable and secure.
            </li>
            <li>
              <strong>Payment/transaction metadata:</strong> when you purchase an itinerary, our payment processor
              Stripe collects billing details and shares back minimal status info so we can unlock your itinerary. We
              do not store full card numbers.
            </li>
          </ul>
        </section>

      {/* 2) How we use data */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">2. How We Use Information</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>Personalize, generate, and display your itinerary.</li>
          <li>Operate, secure, and troubleshoot the Service (fraud prevention, diagnostics, logging).</li>
          <li>Process payments and provide receipts/unlock access.</li>
          <li>Improve quality (e.g., aggregate, de-identified analytics to see what features help travelers most).</li>
          <li>Respond to support requests and honor data rights (access, deletion).</li>
        </ul>
      </section>

      {/* 3) How we share & process data */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">3. How We Share &amp; Process Data</h2>
        <p>
          We use trusted service providers (“sub-processors”) to operate the Service. They process personal information
          on our behalf, under our instructions, and subject to appropriate safeguards.
        </p>

        <h3 className="text-base font-semibold">Core sub-processors (personal data)</h3>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Cloud &amp; databases:</strong> Firebase / Firestore (Google Cloud) —
            stores interview responses, itinerary data, and basic operational logs.
          </li>
          <li>
            <strong>Vector search:</strong> Supabase — used for search / relevance; not used to store interview text or other
            user personal information.
          </li>
          <li>
            <strong>AI generation:</strong> OpenAI — processes your interview inputs to draft itinerary copy.
          </li>
          <li>
            <strong>Payments:</strong> Stripe — processes transactions and shares payment status so we can unlock access.
            We do not store full card numbers.
          </li>
          <li>
            <strong>Diagnostics/analytics:</strong> Lightweight logging and performance tools to keep the Service reliable
            (e.g., IP address, timestamps, errors).
          </li>
        </ul>

        <h3 className="text-base font-semibold">Content infrastructure (no personal data)</h3>
        <p>
          We also use providers to store and/or display our editorial content and assets (e.g., Google Maps, Sanity
          CMS). These systems store our island guides and other editorial content; they do not store interview
          responses or itinerary personal information. Embedded third-party widgets (like maps) may receive your IP
          address when loaded.
        </p>

        <p>
          We do not sell your personal information or share it with third parties for their own marketing. We may update
          our service providers as our stack evolves and will reflect material changes in this section with a revised
          “Last updated” date.
        </p>
      </section>

      {/* 4) Cookies & tracking */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">4. Cookies & Tracking</h2>
        <p>
          We use essential cookies (or similar storage) so the Service works (for example, to keep state in the
          interview and itinerary views). We may use analytics to understand aggregate usage and improve performance.
        </p>
        
      </section>

      {/* 5) Data retention */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">5. Data Retention</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Interviews & itineraries:</strong> retained while your itinerary remains accessible and for at least 12 months after purchase to support trip edits/regeneration, unless you request
            deletion earlier.
          </li>
          <li>
            <strong>Logs & diagnostics:</strong> typically kept for 12 months for security and
            reliability.
          </li>
          <li>
            <strong>Transaction records:</strong> retained as required by law and our payment processor (e.g., tax/audit
            obligations).
          </li>
        </ul>
      </section>

      {/* 6) Your choices & rights */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">6. Your Choices & Data Rights</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <strong>Access & export:</strong> you can request a copy of your interview responses and itinerary.
          </li>
          <li>
            <strong>Correction:</strong> you can update interview answers and we can regenerate the itinerary.
          </li>
          <li>
            <strong>Deletion:</strong> you can ask us to delete your interview, itinerary, and associated identifiers
            (subject to legal retention requirements).
          </li>
          <li>
            <strong>Opt-outs:</strong> if we ever use analytics/ads that require opt-out or “Do Not Sell/Share,” we’ll
            provide a control and update this page.
          </li>
        </ul>
        <p>
          To make a request, use our <a href="/support" className="underline">Support page</a>. We may ask you to verify
          your identity (for example, by confirming the access link or order details) to protect your privacy.
        </p>
      </section>

      {/* 7) Security */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">7. Security</h2>
        <p>
          We use reasonable administrative, technical, and physical safeguards to protect your information. No online
          service can guarantee perfect security, so please keep your unique itinerary/interview links private and let
          us know via the <a href="/support" className="underline">Support page</a> if you believe a link was exposed so
          we can rotate or disable it.
        </p>
      </section>

      {/* 8) Children’s privacy */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">8. Children’s Privacy</h2>
        <p>
          The Service is intended for individuals aged <strong>16 and over</strong>. We do not knowingly collect
          personal information from children under 13. If you believe a child under 13 has provided information, please
          contact us via the <a href="/support" className="underline">Support page</a> so we can delete it.
        </p>
      </section>

      {/* 9) International */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">9. International Users</h2>
        <p>
          Our infrastructure and providers may process data in the United States and other locations. By using the
          Service, you understand your information may be transferred, stored, or processed outside your country of
          residence where laws may be different.
        </p>
      </section>

      {/* 10) Changes */}
      <section className="space-y-5 mt-12 text-sm leading-relaxed text-white/80">
        <h2 className="text-xl font-semibold">10. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy as our Service evolves. Material changes will be noted here with a revised
          “Last updated” date.
        </p>
      </section>

        {/* 11) Contact */}
        <section className="space-y-3 mt-12 text-sm leading-relaxed text-white/80">
          <h2 className="text-xl font-semibold">11. Contact</h2>
          <p>
            Questions or privacy requests? Use our <a href="/support" className="underline">Support page</a>.
          </p>
        </section>
      </div>
    </>
  );
};

export default PrivacyPolicy;
